import type { ActionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import type { Product } from '~/services/api.server';
import { scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { getSession } from '~/services/session.server';
import { isErrorResponse } from '~/utils/common';

export type PartialPart = Product['default_part'] & { product_id: number; product_name: string };

export type FindOrCreatePartActionData = Awaited<ReturnType<Awaited<ReturnType<typeof action>>['json']>>;

export async function action({ request, params }: ActionArgs) {
  const [{ user, headers = new Headers() }, session, formData] = await Promise.all([
    getUpdatedAuthentication(request),
    getSession(request),
    request.formData(),
  ]);
  const accountId = session.get('accountId');
  const productId = params.productIdOrSlug!;
  const attributes = formData.get('attributes') ? JSON.parse(formData.get('attributes')!.toString()) : [];

  const response = await scApi.findOrCreateProductPart(
    user?.accessToken,
    accountId,
    productId,
    { items: attributes },
    { expand: 'supplier_inventories' }
  );
  if (isErrorResponse(response)) {
    return json({ error: 'Failed to find a part with the provided attributes' }, { status: 400, headers });
  }

  const items: Record<number, PartialPart> = Object.fromEntries(
    response.items.map((item) => [
      item.product_id,
      {
        id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        image: item.image,
        blank_image: item.blank_image,
        supplier_inventories: item.supplier_inventories ?? [],
        variant: item.variant,
      },
    ])
  );

  return json({ partHash: response.hash, items }, { headers });
}

export function isError(error: unknown): error is { error: string } {
  return error !== null && typeof error === 'object' && 'error' in error;
}
